import React from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { RiPlaneLine, RiStarSFill, RiAlertLine, RiAlertFill, RiCheckboxCircleFill, RiInformationLine, RiArrowRightLine } from '@remixicon/react';
import { apiClient } from '@/lib/api';
import { getAircraftModelName, getAircraftPhotos } from '@/features/tails/utils';
import { resolveAssetUrl } from '@/utils/urls';
import ImageCarousel from '@components/ui/ImageCarousel';
import { Skeleton } from '@components/ui/Skeleton';
import { useOperatorTrustScore } from '../hooks/useOperatorTrustScore';
import {
  flattenBreakdownComponents,
  buildRiskNarrative,
  getTileVariant,
  STATUS,
} from '../utils/thresholds';
import TrustScoreHero from '../components/TrustScoreHero';
import TrustScoreFactorCard from '../components/TrustScoreFactorCard';

// Icon lookup for the summary tiles' iconKey (star / check / alert / info).
// `alert` uses the OUTLINE variant per Figma so the Caution tile's
// triangle reads as a thin warning glyph rather than a solid block.
const TILE_ICONS = {
  star: RiStarSFill,
  check: RiCheckboxCircleFill,
  alert: RiAlertLine,
  info: RiInformationLine,
};

// Tone → Tailwind classes for the small summary tiles. Icon color hexes
// pinned to Figma tokens: Excellent star = #1FC16B, Caution alert =
// #F6B51E. Backgrounds stay on the Tailwind 100-shade tint scale so
// the tone reads as "washed" and the icon carries the accent.
const TILE_TONES = {
  green: { iconBg: 'bg-green-100', iconText: 'text-[#1FC16B]' },
  blue: { iconBg: 'bg-blue-100', iconText: 'text-blue-600' },
  amber: { iconBg: 'bg-amber-100', iconText: 'text-[#F6B51E]' },
  gray: { iconBg: 'bg-gray-100', iconText: 'text-gray-500' },
};

const SummaryTile = ({ variant, sublabel }) => {
  const tone = TILE_TONES[variant.tone] || TILE_TONES.gray;
  const Icon = TILE_ICONS[variant.iconKey] || RiInformationLine;
  return (
    <div className="flex-1 flex items-start gap-3 min-w-0">
      <div className={`flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center ${tone.iconBg}`}>
        <Icon className={`w-5 h-5 ${tone.iconText}`} />
      </div>
      <div className="min-w-0">
        <p className="text-sm font-semibold text-gray-900 leading-tight">{variant.label}</p>
        <p className="text-xs text-gray-500 leading-snug mt-0.5">{sublabel}</p>
      </div>
    </div>
  );
};

const TrustScoreDetailsPage = () => {
  const { aircraftId } = useParams();

  // Aircraft details drive the left-column presentation (photo, tail,
  // model). We also need the operator_id from here to fire the
  // TrustScore fetch — the aircraft record carries it directly rather
  // than requiring a second lookup.
  const { data: aircraft, isLoading: aircraftLoading } = useQuery({
    queryKey: ['aircraftDetails', aircraftId, 'trust-score-page'],
    queryFn: () => apiClient.getAircraftById(aircraftId, { include: 'make_model, features, operator, photos' }),
    enabled: !!aircraftId,
    staleTime: 0,
    refetchOnMount: 'always',
  });

  const operatorId = aircraft?.operator?.operator_id || aircraft?.operator_id;

  // Operator record — for name/city/state, badges, and fleet count.
  const { data: operator } = useQuery({
    queryKey: ['operator', operatorId, 'trust-score-page'],
    queryFn: () => apiClient.getOperatorById(operatorId, { include: 'trust_scores' }),
    enabled: !!operatorId,
    staleTime: 5 * 60 * 1000,
  });

  const { data: trustScore, isLoading: tsLoading, error: tsError } = useOperatorTrustScore(operatorId);

  const modelName = aircraft ? getAircraftModelName(aircraft) : '';
  const tailNumber = aircraft?.tail_number || '';
  const availability = aircraft?.current_status ? 'Available' : 'Unavailable';

  // Photos — same probe-and-fallback chain the rest of the app uses.
  const photos = aircraft ? getAircraftPhotos(aircraft, true) : null;
  const carouselImages = Array.isArray(photos)
    ? photos.map((u) => resolveAssetUrl(u)).filter(Boolean)
    : (photos ? [resolveAssetUrl(photos)] : []);

  const flatComponents = flattenBreakdownComponents(trustScore?.factors);
  const narrative = buildRiskNarrative(flatComponents);

  // Broker-friendly order: FLAGGED first (needs attention), then
  // ACCEPTABLE (worth a glance), then OPTIMAL (fine). Anything with
  // an unknown status sinks to the bottom. Preserves the original
  // in-tier order (Array.prototype.sort is stable in modern engines).
  const STATUS_ORDER = { [STATUS.FLAGGED]: 0, [STATUS.ACCEPTABLE]: 1, [STATUS.OPTIMAL]: 2 };
  const sortedComponents = [...flatComponents].sort(
    (a, b) => (STATUS_ORDER[a.status] ?? 99) - (STATUS_ORDER[b.status] ?? 99),
  );

  const fleetsCount = operator?.aircraft_count || operator?.fleet_count || null;

  // "Excellent Fleet & Operator Profile" tile → fleet_score
  // "Caution Aircraft Condition & History" tile → tail_score
  const fleetTile = getTileVariant(trustScore?.factors?.fleet_score);
  const tailTile = getTileVariant(trustScore?.factors?.tail_score);

  const operatorLocation = [operator?.faa_city, operator?.faa_state].filter(Boolean).join(', ');       
  return (
    // Standard sub-page wrapper — same translucent rounded card the
    // /tails / QuotesList / AdditionalServicesInvoicePage use. Title +
    // back button live in AppLayout's shared header (fed by
    // setPageHeaderOverride above).
    <div className="pb-24 w-full bg-white/50 backdrop-blur-sm rounded-[2rem] p-4 md:p-6">
      <div className="flex flex-col lg:flex-row gap-4 md:gap-6">
            {/* LEFT column ─────────────────────────────────────────── */}
            <div className="w-full lg:w-96 flex-shrink-0 space-y-4">
              {/* Aircraft photo carousel */}
              <div className="rounded-2xl overflow-hidden bg-gray-100 aspect-[4/3]">
                {aircraftLoading ? (
                  <Skeleton className="w-full h-full" />
                ) : carouselImages.length > 0 ? (
                  <ImageCarousel
                    images={carouselImages}
                    aircraft={aircraft}
                    alt={modelName}
                    className="w-full h-full"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-gray-400">
                    <RiPlaneLine className="w-10 h-10" />
                  </div>
                )}
              </div>

              {/* Tail + availability + model.
                  Availability pill uses design hex tokens — neutral
                  white chip + gray border/text, only the leading icon
                  carries the status color (green when available,
                  orange when not). Icon hex matches the Optimal /
                  Acceptable status colors so it reads as consistent
                  with the factor cards below. */}
              <div>
                <div className="flex items-center gap-2 flex-wrap">
                  <h2 className="text-2xl font-semibold text-gray-900">
                    {aircraftLoading ? <Skeleton className="h-7 w-32" /> : tailNumber || '—'}
                  </h2>
                  {!aircraftLoading && (() => {
                    const isAvailable = availability === 'Available';
                    // Filled variant matches the pill icons in the
                    // factor cards; keeps the eye-line consistent.
                    const AvailabilityIcon = isAvailable ? RiCheckboxCircleFill : RiAlertFill;
                    const iconColor = isAvailable ? '#1FC16B' : '#FA7319';
                    return (
                      <span
                        className="inline-flex items-center gap-1 px-2 py-0.5 bg-[#FFFFFF] text-[#5C5C5C] text-xs rounded-[6px] font-medium border border-[#EBEBEB]"
                      >
                        <AvailabilityIcon className="w-3.5 h-3.5" style={{ color: iconColor }} />
                        {availability}
                      </span>
                    );
                  })()}
                </div>
                <p className="mt-1 text-sm text-gray-600 flex items-center gap-1.5">
                  <RiPlaneLine className="w-4 h-4" />
                  {aircraftLoading ? <Skeleton className="h-4 w-40" /> : modelName || '—'}
                </p>
              </div>

              {/* Summary tiles (derived from tail_score + fleet_score) */}
              <div className="rounded-2xl border border-gray-100 bg-white/70 p-4 flex gap-3">
                <SummaryTile variant={fleetTile} sublabel="Fleet and Operator Profile" />
                <SummaryTile variant={tailTile} sublabel="Aircraft Condition and History" />
              </div>

              {/* Operator card */}
              {operator && (
                <div className="rounded-2xl border border-gray-100 bg-white/70 p-4">
                  <div className="flex items-start gap-3">
                    <div className="min-w-0 flex-1">
                      <h3 className="text-base font-semibold text-gray-900 truncate">
                        {operator.name || 'Operator'}
                      </h3>
                      {operatorLocation && (
                        <p className="text-sm text-gray-500 mt-0.5">{operatorLocation}</p>
                      )}
                    </div>
                    {operator.logo_url && (
                      <img
                        src={resolveAssetUrl(operator.logo_url)}
                        alt=""
                        className="w-10 h-10 rounded-full object-cover flex-shrink-0"
                      />
                    )}
                  </div>
                  <a
                    href={`/operators/${operatorId}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-3 w-full flex items-center justify-between gap-2 px-3 py-2 rounded-xl border border-gray-200 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    Visit Operator Profile
                    <RiArrowRightLine className="w-4 h-4" />
                  </a>
                </div>
              )}
            </div>

            {/* RIGHT column ────────────────────────────────────────── */}
            <div className="flex-1 min-w-0 space-y-3">
              {tsError ? (
                <div className="rounded-2xl border border-red-100 bg-red-50 p-5 text-sm text-red-700">
                  Couldn&apos;t load the TrustScore for this operator. Try again shortly.
                </div>
              ) : tsLoading ? (
                <>
                  <Skeleton className="h-36 w-full rounded-3xl" />
                  {[1, 2, 3, 4, 5, 6].map((i) => (
                    <Skeleton key={i} className="h-14 w-full rounded-2xl" />
                  ))}
                </>
              ) : (
                <>
                  <TrustScoreHero
                    operator={operator}
                    overallScore={trustScore?.overall_score}
                    tier={operator?.rating_tiers || 'Standard'}
                    narrative={narrative}
                    fleetsCount={fleetsCount}
                  />
                  {sortedComponents.length > 0 ? (
                    sortedComponents.map((c) => (
                      <TrustScoreFactorCard
                        key={c.key}
                        title={c.title}
                        body={c.body}
                        status={c.status}
                      />
                    ))
                  ) : (
                    <div className="rounded-2xl border border-gray-100 bg-white p-5 text-sm text-gray-500">
                      No breakdown data available for this operator yet.
                    </div>
                  )}
                </>
              )}
        </div>
      </div>
    </div>
  );
};

export default TrustScoreDetailsPage;
